import type { TabsProps } from '@_/ui';
import { useMemo } from 'react';
import { PiTranslate } from 'react-icons/pi';
import { TbAbc } from 'react-icons/tb';
import { FormattedMessage, useIntl } from 'react-intl';
import { LocaleCodes } from '@_/static';
import { Tab, Tabs } from '@_/ui';
import { formatLanguageName, getLanguageFromLocale } from '@_/utils';
import { HelpTooltip } from '../HelpTooltip/index.ts';

const IconMap = {
  en: <TbAbc className="size-full" />,
  zh: <PiTranslate className="size-full" />
} as const;

const defaultRenderText = (language: string) => (
  <FormattedMessage
    id="component:tab.content-in-language"
    defaultMessage="{language} content"
    values={{ language }}
  />
);

const languages = LocaleCodes.map(locale => getLanguageFromLocale(locale));

export function LanguageTabs({
  disabled,
  language,
  onChange,
  renderText = defaultRenderText
}: Pick<TabsProps, 'disabled' | 'onChange'> & {
  disabled?: boolean;
  language: string;
  renderText?: (value: string) => React.ReactNode;
}) {
  const { locale } = useIntl();

  const value = useMemo(() => getLanguageFromLocale(language), [language]);

  return (
    <Tabs<string> disabled={disabled} onChange={onChange} value={value}>
      {languages.map(lang => (
        <Tab
          accessory={
            <HelpTooltip
              message={
                <FormattedMessage
                  id="component:help.edit-content-in-language"
                  defaultMessage="Content shown to visitors viewing your page in {language}"
                  values={{ language: formatLanguageName(locale, lang) }}
                />
              }
            />
          }
          key={lang}
          value={lang}
        >
          <div className="flex items-center gap-x-2">
            <div className="size-4">{IconMap[lang as keyof typeof IconMap]}</div>

            <span>{renderText(formatLanguageName(locale, lang))}</span>
          </div>
        </Tab>
      ))}
    </Tabs>
  );
}
